import React, { useEffect, useState } from 'react';
import { callApi } from '../../api/callApi';
import { API_ENDPOINT, API_URL_2 } from '../../api/constant';
import Layout from '../../components/Layout/Layout';
import { IProduct, ProductInfo } from '../../interfaces/product';
import useConfirmationModal from '../../hooks/useConfirmationModal';
import ConfirmationModal from '../../components/UI/Modal/ConfirmationModal';
import Pagination from '../../components/UI/List/Pagination';
import { showToastNotification, toFixedThree } from '../../utility/helper';
import { Link, useNavigate } from 'react-router-dom';
import Table from '../../components/UI/CommonTable/CommonTable';
import { ICategory } from '../../interfaces/category';

interface IFilterOption {
    value: string;
    label: string;
}

const Products: React.FC = () => {
    const [products, setProducts] = useState<IProduct[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const { showModal, modalProps, confirmAction } = useConfirmationModal();
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [activeTab, setActiveTab] = useState<'all' | 'low'>('all');
    const [categories, setCategories] = useState<IFilterOption[]>([]);
    const [subCategories, setSubCategories] = useState<ICategory[]>([]);
    const [filteredSubCategories, setFilteredSubCategories] = useState<IFilterOption[]>([]);

    const [filters, setFilters] = useState<{
        categoryId?: string;
        cut?: string;
        title?: string;
        isVatIncluded?: boolean;
    }>({});

    const navigate = useNavigate();

    useEffect(() => {
        fetchDropdownData();
        getProducts(currentPage);
    }, []);

    useEffect(() => {
        getProducts(currentPage);
    }, [activeTab])

    useEffect(() => {
        if (filters.categoryId) {
            const filtered = subCategories
                .filter((subCat) => subCat.categoryId === filters.categoryId)
                .map((subCat) => ({ value: subCat.title, label: subCat.title }));
            setFilteredSubCategories([
                ...filtered,
            ]);
        } else {
            setFilteredSubCategories([]);
        }
    }, [filters.categoryId, subCategories]);

    const getProducts = async (page: number = 1) => {
        try {
            setLoading(true);
            const params = new URLSearchParams();

            // Add pagination and sort params
            params.append('page', page.toString());
            params.append('sortType', 'ASC');

            // Add tab-specific params
            if (activeTab === 'all') {
                params.append('all', 'true');
            } else if (activeTab === 'low') {
                params.append('isLowQuantity', 'true');
            }


            // Add filter params
            if (filters.title) {
                params.append('title', filters.title);
            }
            if (filters.categoryId) {
                params.append('categoryId', filters.categoryId);
            }
            if (filters.cut) {
                params.append('cut', filters.cut);
            }
            if (filters.isVatIncluded !== undefined) {
                params.append('isVatIncluded', filters.isVatIncluded.toString());
            }

            const response = await callApi(
                `${API_URL_2}${API_ENDPOINT.page}products?${params.toString()}`,
                { method: 'GET' }
            );

            const { currentPage: current, data, totalPages: total } = response;
            setProducts(data);
            setCurrentPage(current);
            setTotalPages(total);
        } catch (error: any) {
            console.error('Failed to fetch products:', error.message);
            showToastNotification('danger', 'Failed to fetch products');
        } finally {
            setLoading(false);
        }
    };

    const fetchDropdownData = async () => {
        try {
            const categoriesRes = await callApi(API_URL_2 + API_ENDPOINT.page + "categories", {
                method: "GET",
            });
            setCategories([
                { value: "", label: "Select Category" },
                ...categoriesRes
                    .filter((cat1: ICategory) => cat1.type === "primary")
                    .map((cat: ICategory) => ({ value: cat.id, label: cat.title })),
            ]);
            setSubCategories(
                categoriesRes.filter((cat1: ICategory) => cat1.type === "sub")
            );
        } catch (error: unknown) {
            const errorResponse = error as { message: string };
            console.error("Failed to fetch dropdown data:", errorResponse.message);
        }
    };

    const deleteProduct = async (id: string) => {
        try {
            await callApi(API_URL_2 + API_ENDPOINT.page + `products/${id}`, { method: 'DELETE' });
            showToastNotification('success', 'Product deleted successfully');
            getProducts(currentPage);
        } catch (error: any) {
            console.error('Failed to delete product:', error.message);
            showToastNotification('danger', 'Failed to delete product');
        }
    }

    const handleDeleteClick = (id: string) => {
        confirmAction({
            title: 'Confirm Delete',
            message: 'Are you sure you want to delete this product?',
            onConfirm: () => deleteProduct(id),
        });
    };

    const handlePageChange = (page: number) => {
        setCurrentPage(page);
        getProducts(page);
    };

    const handleFilterChange = (key: keyof typeof filters, value: string | boolean) => {
        setFilters(prev => ({
            ...prev,
            [key]: value === "" ? undefined : value
        }));
    };

    const handleApplyFilters = () => {
        setCurrentPage(1);
        getProducts(1);
    };

    const handleClearFilters = () => {
        setFilters({});
        setCurrentPage(1);
        // Fetch products without filters after a short delay to ensure state is cleared
        setTimeout(() => getProducts(1), 100);
    };

    const columns = [
        {
            header: "Image",
            accessor: "images",
            render: (images: string[]) => (
                <div className='table-image'>
                    <img className="img-image" src={images && images[0]} alt="" />
                </div>
            ),
        },
        { header: "Title", accessor: "title" },
        {
            header: "Price (BHD)",
            accessor: "productInfo",
            render: (productInfo: ProductInfo[]) => toFixedThree(productInfo && productInfo[0]?.price)
        },
        {
            header: "Type Of Meat",
            accessor: "typeOfMeat",
            render: (types: string[]) => types?.join(", ") || "",
        },
        { header: "Cut", accessor: "cut" },
        {
            header: "Available Stock",
            accessor: "productInfo",
            render: (productInfo: ProductInfo[]) => productInfo && productInfo[0]?.quantity
        },
    ];

    const actions = [
        {
            icon: "bi bi-eye",
            color: "warning",
            onClick: (product: IProduct) => navigate(`/product-details/${product.id}`),
        },
        {
            icon: "bi bi-pen",
            color: "success",
            onClick: (product: IProduct) => navigate(`/edit-product/${product.id}`),
        },
        {
            icon: "bi bi-trash3",
            color: "danger",
            onClick: (product: IProduct) => handleDeleteClick(product.id),
        },
    ];

    const downloadReport = async () => {
        try {
            const response = await callApi(API_URL_2 + API_ENDPOINT.page + 'products/export/excel', {
                method: 'GET',
                responseType: 'blob'
            });

            if (!response) {
                throw new Error('Failed to generate report');
            }

            const blob = new Blob([response], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
            const url = window.URL.createObjectURL(blob);

            const a = document.createElement('a');
            a.href = url;
            a.download = `Product_Report_${Date.now()}.xlsx`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Error downloading the report:', error);
            showToastNotification('danger', 'Failed to download report');
        }
    };

    return (
        <Layout>
            <div className='d-flex justify-content-between align-items-start'>
                <h1 className="app-page-title">Products</h1>
                <div className='d-flex gap-2'>
                    <Link to={"/add-product"} className='btn app-btn-primary d-flex align-items-center gap-1'>
                        <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12" fill="none">
                            <path fillRule="evenodd" clipRule="evenodd" d="M6 0C6.13261 0 6.25979 0.0526785 6.35355 0.146447C6.44732 0.240215 6.5 0.367392 6.5 0.5V5.5H11.5C11.6326 5.5 11.7598 5.55268 11.8536 5.64645C11.9473 5.74021 12 5.86739 12 6C12 6.13261 11.9473 6.25979 11.8536 6.35355C11.7598 6.44732 11.6326 6.5 11.5 6.5H6.5V11.5C6.5 11.6326 6.44732 11.7598 6.35355 11.8536C6.25979 11.9473 6.13261 12 6 12C5.86739 12 5.74021 11.9473 5.64645 11.8536C5.55268 11.7598 5.5 11.6326 5.5 11.5V6.5H0.5C0.367392 6.5 0.240215 6.44732 0.146447 6.35355C0.0526785 6.25979 0 6.13261 0 6C0 5.86739 0.0526785 5.74021 0.146447 5.64645C0.240215 5.55268 0.367392 5.5 0.5 5.5H5.5V0.5C5.5 0.367392 5.55268 0.240215 5.64645 0.146447C5.74021 0.0526785 5.86739 0 6 0Z" fill="white" />
                        </svg> Add Product
                    </Link>
                    <button className='btn app-btn-primary d-flex align-items-center gap-1' onClick={downloadReport}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="bi bi-download" viewBox="0 0 16 16">
                            <path d="M.5 9.9a.5.5 0 0 1 .5.5v2.5a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-2.5a.5.5 0 0 1 1 0v2.5a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2v-2.5a.5.5 0 0 1 .5-.5" />
                            <path d="M7.646 11.854a.5.5 0 0 0 .708 0l3-3a.5.5 0 0 0-.708-.708L8.5 10.293V1.5a.5.5 0 0 0-1 0v8.793L5.354 8.146a.5.5 0 1 0-.708.708z" />
                        </svg>
                        Download Report
                    </button>
                </div>
            </div>

            {/* Filter Section */}
            <div className="app-card app-card-orders-table shadow-sm mb-4 p-4">
                <h5 className="mb-3">Filters</h5>
                <div className="row g-3">
                    {/* Title Filter */}
                    <div className="col-md-3">
                        <label htmlFor="titleFilter" className="form-label">Title</label>
                        <input
                            type="text"
                            className="form-control"
                            id="titleFilter"
                            placeholder="Search by title..."
                            value={filters.title || ''}
                            onChange={(e) => handleFilterChange('title', e.target.value)}
                        />
                    </div>

                    {/* Category Filter */}
                    <div className="col-md-3">
                        <label htmlFor="categoryFilter" className="form-label">Category</label>
                        <select
                            className="form-select"
                            id="categoryFilter"
                            value={filters.categoryId || ''}
                            onChange={(e) => handleFilterChange('categoryId', e.target.value)}
                        >
                            {categories.map((category) => (
                                <option key={category.value} value={category.value}>
                                    {category.label}
                                </option>
                            ))}
                        </select>
                    </div>

                    {/* Cut Filter */}
                    <div className="col-md-2">
                        <label htmlFor="cutFilter" className="form-label">Cut</label>
                        <select
                            className="form-select"
                            id="cutFilter"
                            value={filters.cut || ''}
                            onChange={(e) => handleFilterChange('cut', e.target.value)}
                        >
                            <option value="">All Cuts</option>
                            {filteredSubCategories.map((subCat) => (
                                <option key={subCat.value} value={subCat.value}>
                                    {subCat.label}
                                </option>
                            ))}
                        </select>
                    </div>

                    {/* VAT Filter */}
                    <div className="col-md-2">
                        <label htmlFor="vatFilter" className="form-label">VAT Included</label>
                        <select
                            className="form-select"
                            id="vatFilter"
                            value={filters.isVatIncluded === undefined ? '' : filters.isVatIncluded.toString()}
                            onChange={(e) => handleFilterChange('isVatIncluded', e.target.value === 'true')}
                        >
                            <option value="">All</option>
                            <option value="true">Yes</option>
                            <option value="false">No</option>
                        </select>
                    </div>

                    {/* Filter Actions */}
                    <div className="col-md-2 d-flex align-items-end">
                        <div className="d-flex gap-2 w-100">
                            <button
                                className="btn app-btn-primary flex-fill text-nowrap"
                                onClick={handleApplyFilters}
                            >
                                Apply Filters
                            </button>
                            <button
                                className="btn app-btn-secondary"
                                onClick={handleClearFilters}
                                title="Clear all filters"
                            >
                                <i className="bi bi-x-lg"></i>
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Tabs Section */}
            <div className="tabs-container mb-3 gap-2 d-flex ">
                <button
                    className={`tab-button btn app-btn-primary-outline ${activeTab === 'all' ? 'app-btn-primary' : ''}`}
                    onClick={() => {
                        setActiveTab('all');
                        setCurrentPage(1);
                    }}
                >
                    All Products
                </button>
                <button
                    className={`tab-button btn app-btn-primary-outline ${activeTab === 'low' ? 'app-btn-primary' : ''}`}
                    onClick={() => {
                        setActiveTab('low');
                        setCurrentPage(1);
                    }}
                >
                    Low Quantity
                </button>
            </div>

            {/* Products Table */}
            <div className="app-card app-card-orders-table shadow-sm mb-4 p-4">
                <div className="app-card-body">
                    <Table
                        columns={columns}
                        data={products}
                        loading={loading}
                        actions={actions}
                        emptyMessage="No products available."
                    />
                    <Pagination
                        currentPage={currentPage}
                        totalPages={totalPages}
                        onPageChange={handlePageChange}
                    />
                </div>
            </div>
            <ConfirmationModal {...modalProps} show={showModal} />
        </Layout>
    );
};

export default Products;